import {
    Box,
    Container,
    Grid,
    Stack,
    Typography,
    Avatar,
    Skeleton,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "../../data/axios";
import type { PlayerOverview } from "../../types";
import { getChampionImage } from "../../utils/team";

export const Route = createFileRoute("/player/")({
    component: RouteComponent,
});

const formatRole = (role: string | null) => {
    if (!role) return "—";
    const map: Record<string, string> = {
        TOP: "Top",
        JUNGLE: "Jungle",
        MIDDLE: "Mid",
        BOTTOM: "Bot",
        UTILITY: "Support",
        SUPPORT: "Support",
    };
    return map[role.toUpperCase()] ?? role;
};

const PlayerCard = ({ player }: { player: PlayerOverview }) => {
    const navigate = useNavigate();
    const champ = player.mostPlayedChampion;

    return (
        <Box
            onClick={() => navigate({ to: "/player/$playerId", params: { playerId: player.id } })}
            sx={{
                cursor: "pointer",
                display: "block",
                textDecoration: "none",
                position: "relative",
                overflow: "hidden",
                borderRadius: 2,
                backgroundColor: "#161b22",
                border: "1px solid #2a3140",
                transition: "border-color 0.15s ease, transform 0.15s ease",
                "&:hover": {
                    borderColor: "#0a97ab",
                    transform: "translateY(-2px)",
                },
            }}
        >
            {champ && (
                <Box
                    component="img"
                    src={getChampionImage(champ.name)}
                    alt={champ.name}
                    sx={{
                        position: "absolute",
                        right: -30,
                        top: -20,
                        width: 200,
                        height: 200,
                        opacity: 0.18,
                        filter: "blur(1px)",
                        pointerEvents: "none",
                    }}
                />
            )}

            <Stack direction="row" spacing={2} sx={{ p: 2, position: "relative" }}>
                {champ ? (
                    <Avatar
                        src={getChampionImage(champ.name)}
                        alt={champ.name}
                        sx={{
                            width: 84,
                            height: 84,
                            border: "2px solid #2a3140",
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <Avatar sx={{ width: 84, height: 84, bgcolor: "#2a3140", flexShrink: 0 }} />
                )}

                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            color: "#ffffff",
                            fontSize: 18,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                        }}
                        noWrap
                    >
                        {player.summonerName}
                    </Typography>

                    {champ ? (
                        <Typography sx={{ color: "#9ba1b0", fontSize: 12 }}>
                            Mains{" "}
                            <Box component="span" sx={{ color: "#e6e8ee", fontWeight: 600 }}>
                                {champ.name}
                            </Box>{" "}
                            · {champ.games}/{player.gamesPlayed} games
                        </Typography>
                    ) : (
                        <Typography sx={{ color: "#9ba1b0", fontSize: 12 }}>
                            No games yet
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", pt: 0.5 }}>
                        <Typography sx={labelChipSx}>{formatRole(player.primaryRole)}</Typography>
                    </Stack>

                    <Box sx={{ pt: 1 }}>
                        <Typography
                            sx={{
                                color: "#e6e8ee",
                                fontSize: 20,
                                fontWeight: 700,
                                fontFamily: "'Roboto Mono', monospace",
                                lineHeight: 1,
                            }}
                        >
                            {player.gamesPlayed}
                        </Typography>
                        <Typography
                            sx={{
                                color: "#9ba1b0",
                                fontSize: 10,
                                letterSpacing: 1.5,
                                textTransform: "uppercase",
                                mt: 0.25,
                            }}
                        >
                            {player.gamesPlayed === 1 ? "game played" : "games played"}
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
        </Box>
    );
};

const labelChipSx = {
    color: "#0a97ab",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase" as const,
    border: "1px solid #0a97ab",
    borderRadius: 1,
    px: 0.75,
    py: 0.25,
};

const CardSkeleton = () => (
    <Skeleton
        variant="rounded"
        height={140}
        sx={{ bgcolor: "#161b22", borderRadius: 2 }}
    />
);

function RouteComponent() {
    const { data, isLoading } = useQuery<PlayerOverview[]>({
        queryKey: ["players", "overview"],
        queryFn: () => axiosClient.get("/players/overview").then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography
                sx={{
                    color: "#e6e8ee",
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    mb: 0.5,
                }}
            >
                Players
            </Typography>
            <Typography sx={{ color: "#9ba1b0", fontSize: 13, mb: 3 }}>
                Everyone who has played a Staturday Night match.
            </Typography>

            {isLoading ? (
                <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                            <CardSkeleton />
                        </Grid>
                    ))}
                </Grid>
            ) : data && data.length > 0 ? (
                <Grid container spacing={2}>
                    {data.map((p) => (
                        <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <PlayerCard player={p} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Typography sx={{ color: "#9ba1b0" }}>No players yet.</Typography>
            )}
        </Container>
    );
}
